import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  FaArrowRight,
  FaFilePdf,
  FaGithub,
  FaLinkedin,
  FaMusic,
  FaPause,
  FaPlay,
  FaReact,
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi2";
import profilePic from "../../assets/images/profile/profileadam.png";
import portfolioSong from "../../assets/audio/lagu.mp3";
import ProgressiveImage from "../ProgressiveImage";
const roles = ["React Native Developer", "React Developer", "AI Engineer"];
const soundtrackLyrics = [
  "A drag path",
  "etched in the surface",
  "As evidence",
  "I left there on purpose",
  "A sad sap",
  "laying on the surface",
  "(Can you, can you find me?)",
  "Can you find me?",
];

const IntroSection = () => {
  const [role, setRole] = useState(0);
  const [typed, setTyped] = useState("");
  const [isSongPlaying, setIsSongPlaying] = useState(false);
  const [songTime, setSongTime] = useState(0);
  const [songDuration, setSongDuration] = useState(0);
  const audioRef = useRef(null);
  const activeLyric = Math.min(
    soundtrackLyrics.length - 1,
    Math.floor(
      (songTime / Math.max(songDuration, 1)) * soundtrackLyrics.length,
    ),
  );

  const syncSongProgress = (audio) => {
    setSongTime(Number.isFinite(audio.currentTime) ? audio.currentTime : 0);
    if (Number.isFinite(audio.duration) && audio.duration > 0) {
      setSongDuration(audio.duration);
    }
  };

  const toggleSong = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.paused) {
      audio.muted = false;
      if (
        audio.ended ||
        (Number.isFinite(audio.duration) && audio.currentTime >= audio.duration)
      ) {
        audio.currentTime = 0;
      }

      try {
        // Keep this call directly in the tap handler: Safari treats it as the
        // required user gesture for audible media playback.
        await audio.play();
      } catch (error) {
        // Do not call load() here. Reloading after a rejected play interrupts
        // Safari's media pipeline and can leave the next tap unresponsive.
        setIsSongPlaying(false);
      }
    } else {
      audio.pause();
    }
  };

  useEffect(() => {
    const word = roles[role];
    let index = 0;
    setTyped("");
    const typing = setInterval(() => {
      index += 1;
      setTyped(word.slice(0, index));
      if (index === word.length) clearInterval(typing);
    }, 55);
    const next = setTimeout(
      () => setRole((current) => (current + 1) % roles.length),
      2900,
    );
    return () => {
      clearInterval(typing);
      clearTimeout(next);
    };
  }, [role]);

  const soundtrackPlayer = (
    <div
      className={`portfolio-soundtrack ${isSongPlaying ? "is-playing" : ""}`}
      style={{
        "--song-progress": Math.min(1, songTime / Math.max(songDuration, 1)),
      }}
    >
      <div className="soundtrack-player">
        <span className="soundtrack-icon">
          {isSongPlaying ? (
            <span className="soundtrack-wave" aria-hidden="true">
              <i />
              <i />
              <i />
              <i />
            </span>
          ) : (
            <FaMusic />
          )}
        </span>
        <div className="soundtrack-meta">
          {isSongPlaying ? (
            <motion.b
              className="soundtrack-current-lyric"
              key={activeLyric}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {soundtrackLyrics[activeLyric]}
            </motion.b>
          ) : (
            <>
              <b>Drag Path</b>
              <small>Twenty One Pilots</small>
            </>
          )}
        </div>
        <div className="soundtrack-controls">
          <button
            className="soundtrack-toggle"
            type="button"
            onClick={toggleSong}
            aria-label={isSongPlaying ? "Pause soundtrack" : "Play soundtrack"}
          >
            {isSongPlaying ? <FaPause /> : <FaPlay />}
          </button>
        </div>
        <audio
          ref={audioRef}
          src={portfolioSong}
          preload="auto"
          playsInline
          aria-label="Portfolio soundtrack preview"
          onLoadedMetadata={(event) => syncSongProgress(event.currentTarget)}
          onDurationChange={(event) => syncSongProgress(event.currentTarget)}
          onTimeUpdate={(event) => syncSongProgress(event.currentTarget)}
          onSeeking={(event) => syncSongProgress(event.currentTarget)}
          onSeeked={(event) => syncSongProgress(event.currentTarget)}
          onPlay={() => setIsSongPlaying(true)}
          onPause={() => setIsSongPlaying(false)}
          onEnded={() => {
            setIsSongPlaying(false);
            setSongTime(0);
          }}
        />
      </div>
    </div>
  );

  return (
    <section id="home" className="hero hero-editorial">
      <div className="hero-copy-wrap">
        <motion.p
          className="eyebrow"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Cyberjaya · Malaysia <span className="hero-dot" /> Open to
          opportunities
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
        >
          I shape ideas
          <br />
          into <em>useful</em>
          <br />
          digital products.
        </motion.h1>
        <div className="hero-role">
          <HiOutlineSparkles />
          <span>{typed}</span>
        </div>
        <p className="hero-copy">
          Adam Adha Kamarul Zaman — a mobile and web developer with three years
          of production experience and a growing focus in applied AI.
        </p>
        <div className="hero-actions">
          <a className="button-primary" href="#portfolio">
            See projects <FaArrowRight />
          </a>
          <a
            className="button-text resume-button"
            href={`${process.env.PUBLIC_URL}/Resume-Adam-Adha.pdf`}
            download="Resume-Adam-Adha.pdf"
          >
            <span>
              <FaFilePdf />
            </span>{" "}
            Download resume
          </a>
        </div>
        <div className="hero-foot">
          <div className="hero-online">
            <div className="hero-online-row">
              <p className="hero-signature">Find me online</p>
              <div className="socials">
                <a
                  href="https://www.linkedin.com/in/adamadha3/"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="LinkedIn"
                >
                  <FaLinkedin />
                </a>
                <a
                  href="https://github.com/adamadha8"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="GitHub"
                >
                  <FaGithub />
                </a>
              </div>
            </div>
            {soundtrackPlayer}
          </div>
        </div>
      </div>
      <motion.div
        className="hero-visual"
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, delay: 0.1 }}
      >
        <div className="visual-grid" />
        <div className="photo-frame">
          <ProgressiveImage
            src={profilePic}
            alt="Adam Adha Kamarul Zaman"
            loading="eager"
            className="profile-progressive"
          />
          <div className="photo-label">
            ADAM
            <br />
            KZ · 2026
          </div>
        </div>
        <motion.div
          className="floating-card floating-code"
          animate={{ y: [0, -9, 0] }}
          transition={{ duration: 4, repeat: Infinity }}
        >
          <FaReact />
          <span>
            Building with
            <br />
            <b>ReactJS</b>
          </span>
        </motion.div>
        <motion.div
          className="floating-card floating-year"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 4.8, repeat: Infinity }}
        >
          <b>03</b>
          <span>
            years
            <br />
            experience
          </span>
        </motion.div>
      </motion.div>
    </section>
  );
};
export default IntroSection;
